// Components/Services/FullHomeRemodel/HomeRemodelOverview.jsx
import React from "react";
import { motion } from "framer-motion";
import { FiGrid, FiDroplet, FiMaximize } from "react-icons/fi";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const fadeLeft = {
  hidden: { opacity: 0, x: -50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.7, ease: "easeOut" } },
};

const fadeRight = {
  hidden: { opacity: 0, x: 50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.7, ease: "easeOut" } },
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

const remodelTypes = [
  {
    icon: <FiGrid className="w-5 h-5 text-[#212529]" />,
    title: "Kitchen Remodels",
    desc: "Full gut renovations to cabinet refreshes — we redesign your kitchen for function, flow, and beauty.",
  },
  {
    icon: <FiDroplet className="w-5 h-5 text-[#212529]" />,
    title: "Bathroom Renovations",
    desc: "Custom tile work, curbless showers, floating vanities, and premium fixtures installed with precision.",
  },
  {
    icon: <FiMaximize className="w-5 h-5 text-[#212529]" />,
    title: "Whole-Home Transformations",
    desc: "Complete interior overhauls — new flooring, walls, ceilings, lighting, and finishes throughout.",
  },
];

const stats = [
  { number: "$150K+", label: "Average Project Size"     },
  { number: "100%",   label: "Licensed & Insured"       },
  { number: "A–Z",    label: "Project Management"       },
  { number: "3rd",    label: "Generation Contractors"   },
];

const HomeRemodelOverview = () => {
  return (
    <section className="bg-white">

      {/* ── IMAGE + TEXT ── */}
      <div className="max-w-6xl mx-auto px-6 py-14">
        <motion.div
          className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-stretch"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {/* Left — image */}
          <motion.div
            variants={fadeLeft}
            className="relative rounded-2xl overflow-hidden shadow-xl min-h-[380px] group"
            whileHover={{ scale: 1.02 }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
          >
            <img
              src="/Images/full-house-remodel.png"
              alt="Full home remodel by Ashlar Building Group"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
            <div className="absolute bottom-5 left-5">
              <span className="bg-white/90 backdrop-blur-sm text-[#212529] text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full">
                Full Home Remodel
              </span>
            </div>
          </motion.div>

          {/* Right — text */}
          <motion.div variants={fadeRight} className="flex flex-col justify-center">
            <p className="text-[#6c757d] uppercase tracking-widest text-xs font-bold mb-2">
              What We Do
            </p>
            <h3 className="text-2xl md:text-3xl font-black text-[#212529] leading-tight mb-4">
              Your Home, Completely<br />Transformed.
            </h3>
            <p className="text-[#495057] text-sm leading-relaxed mb-3">
              A full home remodel is more than new paint and fixtures. It's a complete transformation — structural changes, new systems, premium finishes, and a home that works exactly the way you live. We manage every phase from demo to final walkthrough with full accountability and zero subcontractor hand-offs.
            </p>
            <p className="text-[#495057] text-sm leading-relaxed mb-5">
              Whether you're updating a single room or overhauling your entire home, Ashlar brings the same third-generation craftsmanship to every square foot. Every material is chosen with longevity in mind. Every detail is executed with precision.
            </p>

            <div className="space-y-3">
              {remodelTypes.map((type, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  whileHover={{ x: 6 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  className="flex items-start gap-4 bg-[#f8f9fa] rounded-xl px-4 py-3 border border-[#dee2e6] cursor-default"
                >
                  <div className="w-9 h-9 rounded-full bg-white border border-[#dee2e6] flex items-center justify-center flex-shrink-0 shadow-sm">
                    {type.icon}
                  </div>
                  <div>
                    <p className="text-[#212529] font-bold text-sm">{type.title}</p>
                    <p className="text-[#6c757d] text-xs mt-0.5 leading-relaxed">{type.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* ── STATS BAR ── */}
      <div className="pb-14 px-4 md:px-6">
        <motion.div
          className="max-w-6xl mx-auto"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <motion.div
            variants={fadeUp}
            className="bg-[#343a40] rounded-2xl shadow-lg px-8 py-8 md:px-12 md:py-10"
          >
            <div className="flex flex-col lg:flex-row items-center gap-6">
              <div className="lg:w-[26%] text-center flex flex-col items-center flex-shrink-0">
                <p className="text-[#dee2e6] text-base font-medium whitespace-nowrap">Transform your home with</p>
                <p className="text-xl md:text-2xl font-bold text-[#f8f9fa] whitespace-nowrap">Expert Remodel Builds</p>
              </div>
              <div className="hidden lg:block w-px h-14 bg-[#495057] flex-shrink-0" />
              <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-4">
                {stats.map((stat, i) => (
                  <motion.div
                    key={i}
                    variants={fadeUp}
                    whileHover={{ scale: 1.1, y: -4 }}
                    transition={{ type: "spring", stiffness: 300, damping: 18 }}
                    className="flex flex-col items-center text-center cursor-default"
                  >
                    <span className="text-xl md:text-2xl font-bold text-[#f8f9fa] mb-1">{stat.number}</span>
                    <p className="text-xs md:text-sm text-[#dee2e6] font-medium whitespace-nowrap">{stat.label}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>

    </section>
  );
};

export default HomeRemodelOverview;